/**
 * RunProfile — the experiment-arm override set for benchmark runs.
 *
 * A profile describes the ONE (or few) variables an experiment arm changes
 * relative to the group's baseline agent definitions. It is applied per
 * launched thread as DURABLE per-session state: the overrides are written to
 * `persistent_agent.profileOverrides` at thread creation and read inside the
 * host's `getAgent` build on EVERY claim — initial start and re-claim after a
 * crash alike — so an arm can never silently revert to baseline mid-scenario
 * (hosts rebuild agents purely from Postgres on re-claim).
 *
 * Only knobs with a real consuming mechanism are representable — the schema
 * is `.strict()` so an unsupported knob is a 400 at start, never a silent
 * no-op that contaminates an experiment:
 *  - `modelConfigId` — replaces the definition's model at build.
 *  - `toolRefs`      — replaces the definition's MCP/tool refs at build.
 *  - `disabledTools` — the NEGATIVE tools arm: subtracts from the effective
 *    set AFTER `toolRefs` and the definition's `toolAllowlists`, so an arm
 *    can drop tools without restating the whole positive list (DEV-17197).
 *  - `instructions`  — replaces the definition's charter at build.
 *  - `memoryMode`    — 'OFF' ablates memory (no # Memory injection AND no
 *    memory tools); 'SEEDED' injects a pinned `memoryFixture` and freezes
 *    writes (the deterministic corpus control); 'LIVE' runs against an
 *    isolated benchmark namespace (DEV-17254). REQUIRED on every
 *    thread-launching Start since DEV-17254 — the benchmark service rejects
 *    a start without an explicit decision; absent is only valid on
 *    non-benchmark threads (no profileOverrides at all). Applied at build
 *    via the durable override path; spawned children inherit it like every
 *    other field.
 *  - `memoryFixture` — the pinned corpus, inlined, required iff SEEDED.
 *
 * Deliberately ABSENT (no consuming mechanism today; add the mechanism first,
 * then the field): a memory `provider` axis (naive/scored/kg — scored & KG
 * retrievers don't exist yet, memory-v4 C/D); temperature/topP/maxOutputTokens
 * (now ModelConfig params — express a sampling arm via `modelConfigId`);
 * reflection/runtime knobs.
 *
 * MEMORY ISOLATION (LIVE mode, DEV-17254): every LIVE Start runs inside a
 * benchmark memory NAMESPACE (`memoryNamespaceId`, service-set) — reads and
 * writes never touch the production store. Trials of one Start share the
 * namespace, so trial N sees what trials 1..N-1 saved (the learning curve,
 * by design); a new Start gets a fresh empty namespace unless the operator
 * names one to continue. For memory-independent trials use
 * `memoryMode: 'OFF'`; the per-build `memoryIdsSnapshot` records exactly
 * what injected.
 */
import { z } from 'zod';
import { MemoryFixtureSchema } from './memory-fixture.js';
export const RunProfileSchema = z
    .object({
    /** Org ModelConfig id to run every launched thread on. */
    modelConfigId: z.string().min(1).optional(),
    /** Replacement toolRefs (MCP connection names / platform tool groups). */
    toolRefs: z.array(z.string().min(1)).max(50).optional(),
    /**
     * The NEGATIVE arm of the tools axis (DEV-17197). Applied AFTER
     * `toolRefs` (override or baseline) and AFTER the definition's own
     * `toolAllowlists`, so an arm can subtract from baseline without
     * restating the whole positive list — `toolRefs` alone cannot express
     * "same tools minus one".
     *
     * Each id is one of:
     *  - `"<connectionName>/<toolName>"` — deny exactly that tool on that
     *    MCP connection.
     *  - `"<ref>"` (no slash) — drop the whole ref from the effective
     *    toolRefs. Covers both "disable an entire MCP connection" and
     *    "disable a platform tool id".
     */
    disabledTools: z.array(z.string().min(1)).max(200).optional(),
    /** Replacement system prompt (charter) for every launched thread. */
    instructions: z.string().min(1).max(100_000).optional(),
    /**
     * The memory arm for every launched thread and its spawned children:
     *  - 'OFF'    — memory ablated: no # Memory block injected and no memory
     *    tools registered (the thread can neither read nor form memories).
     *  - 'SEEDED' — a PINNED corpus: inject exactly `memoryFixture.rows` (the
     *    naive cap-15 ordering) and FREEZE writes (save_memory is a no-op,
     *    search is scoped to the fixture). The deterministic memory control —
     *    identical corpus every trial, so a comparison varies only the corpus,
     *    never run-to-run store drift. Requires `memoryFixture`.
     *  - 'LIVE' — the live unified store, scoped to this run's isolated
     *    benchmark namespace (`memoryNamespaceId`, DEV-17254) — never the
     *    production store. REQUIRED (with OFF/SEEDED as the alternatives)
     *    on every thread-launching benchmark Start; absent is only the
     *    non-benchmark default.
     * Wired at the host's memory-injection seam. 'LIVE' does NOT re-enable
     * memory on a definition whose debug config sets `memoryInjectionDisabled`
     * (the switches are OR'd — either one ablates).
     *
     * There is deliberately NO `provider` (naive/scored/kg) field: the scored
     * and KG retrievers do not exist yet (memory-v4 C/D), and the `.strict()`
     * contract only represents knobs with a real consuming mechanism. SEEDED
     * renders through the existing naive path.
     */
    memoryMode: z.enum(['OFF', 'LIVE', 'SEEDED']).optional(),
    /**
     * Replacement save-guidance block (DEV-17246, §4.9) for every launched
     * thread: overrides the versioned default (`memorySaveGuidanceBlock`)
     * inserted into the PA system prompt wherever memory tools are wired, so
     * "did changing the save guidance improve task outcome?" is a runnable
     * one-lever arm. The effective guidance's identity (default version id or
     * `p_<hash>` of this override) is captured on the resolvedProfile and
     * hashed into the config fingerprint's memory axis.
     */
    saveGuidance: z.string().min(1).max(20_000).optional(),
    /**
     * The pinned corpus for `memoryMode: 'SEEDED'`. Inlined (not a ref) so a
     * benchmark run is fully self-describing — the whole fixture, including its
     * `fixtureHash`, is captured in `BenchmarkRun.resolvedProfile`, making the
     * arm's corpus auditable without a separate fixture store. Required iff
     * SEEDED; rejected otherwise.
     */
    memoryFixture: MemoryFixtureSchema.optional(),
    /**
     * The memory namespace the run's threads write into and resolve from
     * (DEV-17254, §6.5). Set BY THE BENCHMARK SERVICE at start (a fresh
     * namespace by default, or an operator-named continuation) — never by
     * the operator directly on the profile; it rides `profileOverrides` so
     * every claim of every launched thread lands in the same namespace.
     * Only meaningful with memoryMode 'LIVE' (OFF reads/writes nothing,
     * SEEDED is a frozen fixture).
     */
    memoryNamespaceId: z.string().min(1).optional(),
    /**
     * The morning-after capture lever (DEV-17324): whether the
     * TrialCaptureService diffs sim ground truth after each COMPLETED trial
     * and writes the lessons into the run's memory namespace before the next
     * trial launches. Default ON for 'LIVE' runs (arm C of the DEV-17260
     * protocol); set false for the capture-ablated arm (C-prime: same
     * carryover, agents teach only themselves). Only meaningful with
     * memoryMode 'LIVE' — capture writes into the namespace, and OFF/SEEDED
     * runs carry none.
     */
    trialCapture: z.boolean().optional(),
})
    .strict()
    .superRefine((p, ctx) => {
    if (p.memoryMode === 'SEEDED' && !p.memoryFixture) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['memoryFixture'],
            message: "memoryMode 'SEEDED' requires a memoryFixture (the pinned corpus to inject).",
        });
    }
    if (p.memoryMode !== 'SEEDED' && p.memoryFixture) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['memoryFixture'],
            message: "memoryFixture is only valid with memoryMode 'SEEDED'.",
        });
    }
    if (p.memoryNamespaceId !== undefined && p.memoryMode !== 'LIVE') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['memoryNamespaceId'],
            message: "memoryNamespaceId is only valid with memoryMode 'LIVE' (DEV-17254).",
        });
    }
    if (p.trialCapture !== undefined && p.memoryMode !== 'LIVE') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['trialCapture'],
            message: "trialCapture is only valid with memoryMode 'LIVE' (DEV-17324) — capture writes into the run's namespace.",
        });
    }
});
/** ModelConfig snapshot stamped on a run so the UI can show the model without joins. */
export const ResolvedRunModelSchema = z.object({
    modelConfigId: z.string(),
    /** Org ModelConfig display name at capture time. */
    name: z.string(),
    provider: z.string(),
    /** Provider model id (e.g. claude-sonnet-5, accounts/fireworks/…). */
    modelId: z.string(),
    /** True when profile.modelConfigId overrode definition baseline(s). */
    fromProfile: z.boolean(),
});
/**
 * What a benchmark run RECORDS about the configuration that actually ran —
 * evidence-grade: the requested profile plus each definition's baseline at
 * launch time, so results are attributable without asking anyone what they
 * ran, and a baseline drift between arms is detectable after the fact.
 */
export const ResolvedRunProfileSchema = z.object({
    /** The validated profile the run was started with ({} = baseline arm). */
    profile: RunProfileSchema,
    /** Per-definition baseline the overrides applied against, at launch. */
    baseline: z
        .array(z.object({
        agentDefinitionId: z.string(),
        modelConfigId: z.string().nullable(),
        toolRefs: z.array(z.string()),
        /**
         * Content-address (`p_<hash>`) of this definition's prompt/charter at
         * launch — lets the config fingerprint's prompt axis detect a prompt
         * change without storing the full (up to 100k-char) text. Optional so
         * pre-field resolvedProfile literals keep parsing.
         */
        promptHash: z.string().nullable().optional(),
        /**
         * Content-address of this definition's custom principle-judge prompt
         * (`config.reflection.judgePrompt`, PR #1123) at launch; null = the
         * built-in default. Judge prompts are fingerprinted prompt assets
         * (DEV-17246, A.1.32 rule 5). Optional so pre-field literals parse.
         */
        judgePromptHash: z.string().nullable().optional(),
    }))
        .default([]),
    /**
     * Effective model for this run (profile override if set, else the single
     * shared baseline modelConfig). Null when no model is configured or
     * baselines disagree across launched definitions.
     */
    model: ResolvedRunModelSchema.nullable().optional(),
    /**
     * True when this run's captured baseline differs from the FIRST run of the
     * same armId — someone edited a definition mid-experiment. Recorded, not
     * blocked: the run proceeds, but compare/console can discount it.
     */
    baselineDrift: z.boolean().optional(),
    /** Run id of the arm's first run whose baseline this one drifted from. */
    baselineDriftFromRunId: z.string().optional(),
    /** Human-readable per-definition diff lines ("agent X: modelConfigId a→b"). */
    baselineDriftSummary: z.array(z.string()).optional(),
    /**
     * Identity of the save-guidance block this run's threads carried
     * (DEV-17246): the default block's version id (`sg1`, …) or the `p_<hash>`
     * of `profile.saveGuidance`. Captured at creation — absent on legacy runs,
     * which the fingerprint then reports as uncaptured (null) rather than
     * retroactively claiming a guidance version they never ran.
     */
    saveGuidanceVersion: z.string().optional(),
    /**
     * The memory namespace this run's threads wrote into (DEV-17254) — the
     * corpus identity the fingerprint's memory axis records. Absent on legacy
     * runs and on OFF/SEEDED/classical runs (no namespace carried).
     */
    memoryNamespaceId: z.string().optional(),
    /**
     * The scenario draw seed THIS run actually sent to the sim (DEV-17328) —
     * the per-trial derived value, not the Start's base seed. Write-once
     * evidence ("were these two arm runs draw-paired?"); absent when the Start
     * carried no seed. Deliberately not a fingerprint axis (see
     * StartBenchmarkInputSchema.scenarioSeed).
     */
    scenarioSeed: z.string().optional(),
});
//# sourceMappingURL=run-profile.js.map